import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { docSetupApi, FL020 } from '../../../services/docSetupApi';

export type Mode = 'browse' | 'add' | 'edit';

export function useDocSetupForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<FL020[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState('');
  const [deptCode, setDeptCode] = useState('');
  const [docName, setDocName] = useState('');
  const [docType, setDocType] = useState('');
  const [creator, setCreator] = useState('');

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try { const res = await docSetupApi.list(1, 1000); setRecords(res.data || []); return res.data || []; }
    catch { message.error('載入失敗'); return []; }
    finally { setLoading(false); }
  }, []);

  const syncFields = useCallback((r: FL020) => {
    setCode(r.fl020_01); setDeptCode(r.fl020_10); setDocName(r.fl020_11);
    setDocType(r.fl020_16); setCreator(r.fl020_18);
  }, []);

  useEffect(() => { if (current && mode === 'browse') syncFields(current); }, [current, mode, syncFields]);
  useEffect(() => { loadRecords().then(d => { if (d.length > 0) setCurrentIdx(0); }); }, [loadRecords]);

  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  const startAdd = () => { setMode('add'); setCode(''); setDeptCode(''); setDocName(''); setDocType(''); setCreator(''); };
  const startEdit = () => { if (!current) return; setMode('edit'); syncFields(current); };
  const cancel = () => { setMode('browse'); if (current) syncFields(current); };

  const save = async () => {
    try {
      if (mode === 'add') {
        await docSetupApi.create({ fl020_01: code, fl020_10: deptCode, fl020_11: docName, fl020_16: docType, fl020_18: creator });
        message.success('新增成功');
      } else {
        await docSetupApi.update(current!.fl020_01, { fl020_10: deptCode, fl020_11: docName, fl020_16: docType, fl020_18: creator });
        message.success('修改成功');
      }
      const data = await loadRecords();
      const t = mode === 'add' ? code : current!.fl020_01;
      const idx = data.findIndex(r => r.fl020_01 === t);
      setCurrentIdx(idx >= 0 ? idx : 0); setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) vErrs.forEach((e: any) => message.error(e.message));
      else message.error(err?.response?.data?.error || '儲存失敗');
    }
  };

  const doDelete = async () => {
    if (!current) return;
    try { await docSetupApi.delete(current.fl020_01); message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) setCurrentIdx(Math.min(currentIdx, data.length - 1)); else setCurrentIdx(-1);
    } catch { message.error('刪除失敗'); }
  };

  const quickSearch = (t: string) => {
    if (isEditing) return;
    const exact = records.findIndex(r => r.fl020_01 === t);
    if (exact >= 0) { setCurrentIdx(exact); return; }
    const near = records.findIndex(r => r.fl020_01 >= t);
    if (near >= 0) { setCurrentIdx(near); message.info(`定位至最近的「${records[near].fl020_01}」`); }
    else { message.warning('查無此代號'); }
  };

  return { mode, isEditing, loading, records, current, currentIdx,
    code, setCode, deptCode, setDeptCode, docName, setDocName, docType, setDocType, creator, setCreator,
    navFirst, navPrev, navNext, navLast, startAdd, startEdit, cancel, save, doDelete, quickSearch };
}
